/**
 * Customers → Import: register many customers from an Excel or CSV sheet.
 * The upload is a preview first; nothing is saved until "Register".
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, FileUp, UserPlus } from 'lucide-react';
import { ApiError, getAccessToken } from '../../lib/api';
import { Alert, Button, Modal, StatusChip, TableWrap, Td, Th } from '../../components/ui';

interface ResultRow {
  row: number;
  name: string;
  status: 'create' | 'existing' | 'duplicate' | 'error';
  code?: string;
  filled?: string[];
  contact?: 'create' | 'existing' | null;
  message?: string;
}
interface ImportResponse {
  dryRun: boolean;
  summary: { total: number; create: number; existing: number; duplicate: number; error: number };
  rows: ResultRow[];
}

const TONE = { create: 'ok', existing: 'neutral', duplicate: 'warning', error: 'critical' } as const;
const LABEL = { create: 'New', existing: 'Already on file', duplicate: 'Repeated in sheet', error: 'Problem' } as const;

async function send(file: File, dryRun: boolean): Promise<ImportResponse> {
  const body = new FormData();
  body.append('file', file);
  body.append('dryRun', String(dryRun));
  const response = await fetch('/api/v1/customers/import', {
    method: 'POST',
    credentials: 'include',
    headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    body,
  });
  const payload = (await response.json().catch(() => null)) as (ImportResponse & { message?: string | string[] }) | null;
  if (!response.ok) {
    const message = Array.isArray(payload?.message) ? payload?.message[0] : payload?.message;
    throw new ApiError(response.status, message ?? 'The import failed.');
  }
  return payload as ImportResponse;
}

export function CustomerImportModal({ onClose }: { onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (dryRun: boolean): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const response = await send(file, dryRun);
      setResult(response);
      if (!dryRun) void queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The import failed.');
    } finally {
      setBusy(false);
    }
  };

  const template = async (): Promise<void> => {
    const response = await fetch('/api/v1/customers/import/template', {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) return setError('Could not download the template.');
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ITCORE-SF_Customer_Import_Template.xlsx';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  const done = result && !result.dryRun;
  const s = result?.summary;

  return (
    <Modal
      title="Import customers"
      description="Excel (.xlsx) or CSV, one customer per row. Matched by TIN, then by name; existing customers are never overwritten."
      onClose={onClose}
    >
      <div className="space-y-4">
        {error && <Alert tone="critical">{error}</Alert>}
        {!done && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 text-sm hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
              <FileUp aria-hidden="true" className="h-4 w-4" />
              <span className="max-w-[16rem] truncate">{file ? file.name : 'Choose file'}</span>
              <input
                type="file"
                className="sr-only"
                accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setResult(null);
                }}
              />
            </label>
            <Button variant="ghost" onClick={() => void template()}>
              <Download aria-hidden="true" className="h-4 w-4" /> Template
            </Button>
            <Button variant="secondary" disabled={!file || busy} busy={busy && !result} onClick={() => void run(true)}>
              Preview
            </Button>
          </div>
        )}

        {s && (
          <Alert tone={done ? 'ok' : s.error ? 'warning' : 'info'}>
            {done ? 'Registered: ' : 'Preview: '}
            {s.create} new, {s.existing} already on file
            {s.duplicate ? `, ${s.duplicate} repeated` : ''}
            {s.error ? `, ${s.error} with problems (skipped)` : ''}.
          </Alert>
        )}

        {result && (
          <div className="max-h-[50vh] overflow-auto">
            <TableWrap>
              <thead>
                <tr>
                  <Th>Row</Th>
                  <Th>Customer</Th>
                  <Th>Result</Th>
                  <Th>Note</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {result.rows.map((row) => (
                  <tr key={row.row}>
                    <Td>
                      <span className="text-xs text-slate-500">{row.row}</span>
                    </Td>
                    <Td>
                      <div>{row.name || '—'}</div>
                      {row.code && <div className="font-mono text-xs text-slate-500">{row.code}</div>}
                    </Td>
                    <Td>
                      <StatusChip tone={TONE[row.status]}>{LABEL[row.status]}</StatusChip>
                    </Td>
                    <Td>
                      <span className="text-xs text-slate-600 dark:text-slate-300">
                        {[
                          row.message,
                          row.filled?.length ? `fills ${row.filled.join(', ')}` : null,
                          row.contact === 'create' ? 'adds contact' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </Button>
          {result?.dryRun && (
            <Button disabled={busy || (s?.create ?? 0) + (s?.existing ?? 0) === 0} busy={busy} onClick={() => void run(false)}>
              <UserPlus aria-hidden="true" className="h-4 w-4" /> Register {s?.create ?? 0} new
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
